/**
 * `/admin/feedback` — what people thought of the assistant's replies.
 *
 * A review screen, deliberately small: the 👍 vs 👎 counts for the last 7 and
 * 30 days, then the latest verdicts as a table with the reply and the question
 * that prompted it. There is no user column and nothing links onward — the
 * review is of the assistant, not of the person, and the excerpts are all an
 * admin needs to recognise the turn. A reply whose conversation was deleted
 * shows no excerpt; the server withholds it.
 *
 * `?view=errors` switches to the auto-logged errors (8 Oct,
 * `admin-error-feedback.tsx`); `&ref=` narrows that to one reference.
 * `?view=leadership` lists the comments sent from the Leadership report
 * (8 Oct, `admin-leadership-feedback.tsx`).
 *
 * Every view lists open items by default (9 Oct, `resolution-controls.tsx`):
 * `?status=resolved|all` switches, and each row can be resolved with a fix
 * note or reopened.
 *
 * 12 Oct: fits one screen at 1366–1920 px — up to 1600 px wide, KPIs and
 * filters each on one row, a compact table with two-line excerpts whose full
 * text opens under the row on click, and a sticky header (`compact-cells.tsx`).
 */

import {
  Chip,
  ChipGroup,
  DataGrid,
  type DataGridColumn,
  Pagination,
  StatisticBar,
  Typography,
} from "@norican/react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import {
  AppBreadcrumbs,
  EmptyStateBlock,
  ErrorState,
  LoadingRows,
  PageContainer,
} from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import type { AdminFeedbackEntry, FeedbackCounts, FeedbackRating } from "@/lib/api";

import { AdminErrorFeedback } from "./admin-error-feedback";
import { AdminLeadershipFeedback } from "./admin-leadership-feedback";
import { useAdminFeedback, useAdminFeedbackSummary } from "./api";
import {
  Clamp,
  COMPACT_GRID_CLASS,
  DetailField,
  DetailPanel,
  RatingIcon,
  ReasonChips,
  ShortDateTime,
} from "./compact-cells";
import { helpfulPercent } from "./feedback-draft";
import {
  EmptyForStatus,
  ResolutionFixCell,
  ResolutionStatusCell,
  StatusFilterChips,
} from "./resolution-controls";
import { DEFAULT_STATUS_FILTER, type StatusFilter, statusFilterOf } from "./resolution-helpers";

const PAGE_SIZE = 25;

type RatingFilter = "ALL" | FeedbackRating;

const RATING_FILTERS: readonly RatingFilter[] = ["ALL", "HELPFUL", "NOT_HELPFUL"];

/**
 * The views of the inbox: the assistant review, the auto-logged errors (8 Oct),
 * and comments sent from the Leadership report (8 Oct).
 */
type InboxView = "feedback" | "errors" | "leadership";
const INBOX_VIEWS: readonly InboxView[] = ["feedback", "errors", "leadership"];

function viewOf(raw: string | null): InboxView {
  return raw === "errors" || raw === "leadership" ? raw : "feedback";
}

function Excerpt({ text }: { text: string | null | undefined }) {
  const { t } = useT("assistant");
  return text ? (
    <Clamp>{text}</Clamp>
  ) : (
    <Typography variant="caption" tone="muted">
      {t("feedbackAdmin.noExcerpt")}
    </Typography>
  );
}

/** Everything about one verdict, full length: opens under the row on click. */
function FeedbackDetail({ row }: { row: AdminFeedbackEntry }) {
  const { t } = useT("assistant");
  const none = (
    <Typography variant="caption" tone="muted">
      {t("feedbackAdmin.noExcerpt")}
    </Typography>
  );
  return (
    <DetailPanel>
      <DetailField label={t("feedbackAdmin.column.question")}>
        {row.question_excerpt || none}
      </DetailField>
      <DetailField label={t("feedbackAdmin.column.reply")}>{row.reply_excerpt || none}</DetailField>
      <DetailField label={t("feedbackAdmin.column.comment")}>
        <span className="flex flex-col gap-1">
          <ReasonChips codes={row.reason_codes} />
          {row.comment ?? null}
        </span>
      </DetailField>
    </DetailPanel>
  );
}

export function AdminFeedbackPage() {
  const { t } = useT("assistant");
  useDocumentTitle(t("feedbackAdmin.documentTitle"));

  const [searchParams, setSearchParams] = useSearchParams();
  const view = viewOf(searchParams.get("view"));
  const refFilter = searchParams.get("ref")?.trim().toUpperCase() || null;
  const status = statusFilterOf(searchParams.get("status"));
  const [filter, setFilter] = useState<RatingFilter>("ALL");
  const [page, setPage] = useState(1);

  function setStatus(next: StatusFilter) {
    const params = new URLSearchParams(searchParams);
    // Picking a status leaves a Teams card's single-reference view.
    params.delete("ref");
    if (next === DEFAULT_STATUS_FILTER) {
      params.delete("status");
    } else {
      params.set("status", next);
    }
    setSearchParams(params);
    setPage(1);
  }

  const summary = useAdminFeedbackSummary();
  const list = useAdminFeedback({
    rating: filter === "ALL" ? null : filter,
    status,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });

  // Minimum widths: the grid stretches them to fill the table and scrolls
  // sideways if they add up to more, so they total under 1300 px (1366 screen).
  const columns = useMemo<DataGridColumn<AdminFeedbackEntry>[]>(() => {
    const detail = (row: AdminFeedbackEntry) => <FeedbackDetail row={row} />;
    return [
      {
        id: "date",
        header: t("feedbackAdmin.column.date"),
        width: 96,
        cell: (row) => <ShortDateTime value={row.updated_at} />,
      },
      {
        id: "rating",
        header: <span className="sr-only">{t("feedbackAdmin.column.rating")}</span>,
        width: 40,
        align: "center",
        cell: (row) => <RatingIcon rating={row.rating} />,
      },
      {
        id: "reasons",
        header: t("feedbackAdmin.column.reasons"),
        width: 150,
        cell: (row) => <ReasonChips codes={row.reason_codes} />,
      },
      {
        id: "comment",
        header: t("feedbackAdmin.column.comment"),
        width: 200,
        cell: (row) => (row.comment ? <Clamp>{row.comment}</Clamp> : null),
        renderCellDetail: detail,
      },
      {
        id: "reply",
        header: t("feedbackAdmin.column.reply"),
        width: 280,
        cell: (row) => <Excerpt text={row.reply_excerpt} />,
        renderCellDetail: detail,
      },
      {
        id: "question",
        header: t("feedbackAdmin.column.question"),
        width: 210,
        cell: (row) => <Excerpt text={row.question_excerpt} />,
        renderCellDetail: detail,
      },
      {
        id: "status",
        header: t("feedbackAdmin.resolution.column.status"),
        width: 70,
        align: "center",
        cell: (row) => <ResolutionStatusCell resolution={row.resolution} />,
      },
      {
        id: "fix",
        header: t("feedbackAdmin.resolution.column.fix"),
        width: 170,
        cell: (row) => (
          <ResolutionFixCell
            kind="feedback"
            itemKey={row.feedback_id}
            resolution={row.resolution}
            subject={row.comment ?? row.question_excerpt ?? ""}
          />
        ),
      },
    ];
  }, [t]);

  const items = list.data?.items ?? [];
  const total = list.data?.page.total ?? 0;

  function windowItems(counts: FeedbackCounts) {
    const percent = helpfulPercent(counts.helpful ?? 0, counts.total ?? 0);
    const caption =
      percent === null
        ? t("feedbackAdmin.noRatings")
        : t("feedbackAdmin.helpfulShare", { percent });
    return [
      {
        label: t("feedbackAdmin.helpfulIn", { days: counts.days }),
        value: String(counts.helpful ?? 0),
        tone: "positive" as const,
        caption,
      },
      {
        label: t("feedbackAdmin.notHelpfulIn", { days: counts.days }),
        value: String(counts.not_helpful ?? 0),
        tone: "negative" as const,
      },
    ];
  }

  const viewChips = (
    <span className="flex items-center gap-2">
      <Typography variant="label" id="feedback-view">
        {t("feedbackAdmin.view.label")}
      </Typography>
      <ChipGroup.Root
        aria-labelledby="feedback-view"
        value={[view]}
        onValueChange={(value) => {
          const next = value[0] as InboxView | undefined;
          if (next !== undefined && next !== view) {
            setSearchParams(next === "feedback" ? {} : { view: next });
            setPage(1);
          }
        }}
      >
        {INBOX_VIEWS.map((option) => (
          <Chip key={option} value={option} size="sm">
            {t(`feedbackAdmin.view.${option}`)}
          </Chip>
        ))}
      </ChipGroup.Root>
    </span>
  );

  const kpis =
    view !== "feedback" ? undefined : summary.isLoading ? (
      <LoadingRows rows={1} />
    ) : summary.error != null ? (
      <ErrorState variant="inline" error={summary.error} onRetry={() => void summary.refetch()} />
    ) : summary.data === undefined ? undefined : (
      <StatisticBar
        size="sm"
        aria-label={t("feedbackAdmin.summaryLabel")}
        items={[
          {
            label: t("feedbackAdmin.resolution.openItems"),
            value: list.data === undefined ? "–" : String(list.data.counts?.open ?? 0),
            caption: t("feedbackAdmin.resolution.resolvedCaption", {
              count: list.data?.counts?.resolved ?? 0,
            }),
          },
          ...windowItems(summary.data.last_7_days),
          ...windowItems(summary.data.last_30_days),
        ]}
      />
    );

  return (
    <PageContainer
      title={t("feedbackAdmin.title")}
      subtitle={t("feedbackAdmin.subtitle")}
      breadcrumbs={<AppBreadcrumbs items={[{ label: t("feedbackAdmin.title") }]} />}
      actions={kpis}
      width="screen"
      dense
    >
      {view === "leadership" ? (
        <AdminLeadershipFeedback toolbar={viewChips} status={status} onStatusChange={setStatus} />
      ) : view === "errors" ? (
        <AdminErrorFeedback
          toolbar={viewChips}
          refFilter={refFilter}
          onShowAll={() => setSearchParams({ view: "errors" })}
          status={status}
          onStatusChange={setStatus}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {viewChips}
            <span className="flex items-center gap-2">
              <Typography variant="label" id="feedback-rating-filter">
                {t("feedbackAdmin.filterLabel")}
              </Typography>
              <ChipGroup.Root
                aria-labelledby="feedback-rating-filter"
                value={[filter]}
                onValueChange={(value) => {
                  const next = value[0] as RatingFilter | undefined;
                  if (next !== undefined && next !== filter) {
                    setFilter(next);
                    setPage(1);
                  }
                }}
              >
                {RATING_FILTERS.map((option) => (
                  <Chip key={option} value={option} size="sm">
                    {t(`feedbackAdmin.filter.${option}`)}
                  </Chip>
                ))}
              </ChipGroup.Root>
            </span>
            <StatusFilterChips
              id="feedback-status-filter"
              value={status}
              counts={list.data?.counts}
              onChange={setStatus}
            />
          </div>

          {list.isLoading ? (
            <LoadingRows rows={5} />
          ) : list.error != null ? (
            <ErrorState error={list.error} onRetry={() => void list.refetch()} />
          ) : items.length === 0 ? (
            <EmptyForStatus status={status} counts={list.data?.counts}>
              <EmptyStateBlock
                title={t("feedbackAdmin.empty.title")}
                description={t("feedbackAdmin.empty.hint")}
              />
            </EmptyForStatus>
          ) : (
            <>
              <DataGrid
                aria-label={t("feedbackAdmin.tableLabel")}
                columns={columns}
                data={items}
                getRowId={(row) => row.feedback_id}
                tableLayout="fixed"
                density="compact"
                className={COMPACT_GRID_CLASS}
              />
              <Pagination
                page={page}
                total={total}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
                label={t("feedbackAdmin.pagination")}
                prevLabel={t("feedbackAdmin.previous")}
                nextLabel={t("feedbackAdmin.next")}
                formatStatus={({ page: current, pageCount }) =>
                  t("feedbackAdmin.pageStatus", { page: current, pages: pageCount })
                }
                formatSummary={(range) =>
                  t("feedbackAdmin.rangeSummary", {
                    from: range.from,
                    to: range.to,
                    total: range.total,
                  })
                }
              />
            </>
          )}
        </>
      )}
    </PageContainer>
  );
}
